import { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronRight,
  faCopy,
  faMagnifyingGlass,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import { Input } from '@/components/ui/input';

/**
 * Grouped, searchable variable picker shared by the tenant Settings templates
 * page and the Platform Console Email Template Library
 * (COMMUNICATION_DESIGN.md §5.4 / §5.7 — D3).
 *
 * Extracted from CommunicationTemplatesPage. The only behavioural addition is the
 * search field required by the Phase B brief; grouping, auto-expand and
 * click-to-insert are unchanged, so the tenant page renders as before.
 *
 * The variable shape is declared structurally rather than imported from the
 * tenant service, so the Platform Console does not depend on tenant-portal code.
 */
export interface TemplateVariable {
  key: string;
  label: string;
  module: string;
  exampleValue?: string;
  description?: string;
}

interface VariableBrowserProps {
  variables: TemplateVariable[];
  onInsert: (key: string) => void;
  loading?: boolean;
}

export function VariableBrowser({ variables, onInsert, loading }: VariableBrowserProps) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return variables;
    return variables.filter(
      (v) =>
        v.key.toLowerCase().includes(q) ||
        v.label.toLowerCase().includes(q) ||
        (v.description ?? '').toLowerCase().includes(q),
    );
  }, [variables, query]);

  const grouped = useMemo(
    () =>
      filtered.reduce<Record<string, TemplateVariable[]>>((acc, v) => {
        if (!acc[v.module]) acc[v.module] = [];
        acc[v.module]!.push(v);
        return acc;
      }, {}),
    [filtered],
  );

  const modules = Object.keys(grouped);

  // Auto-expand the first group on load.
  useEffect(() => {
    if (modules.length > 0 && Object.keys(expanded).length === 0) {
      setExpanded({ [modules[0]!]: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variables.length]);

  const toggle = (module: string) => {
    setExpanded((prev) => ({ ...prev, [module]: !prev[module] }));
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (variables.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 py-8 text-center">
        <p className="text-xs text-muted-foreground">No variables registered yet.</p>
        <p className="text-[11px] text-muted-foreground/70">
          Modules register variables at startup.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative">
        <FontAwesomeIcon
          icon={faMagnifyingGlass}
          className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search variables…"
          aria-label="Search variables"
          className="h-8 pl-8 text-xs"
        />
      </div>

      {modules.length === 0 ? (
        <p className="py-6 text-center text-xs text-muted-foreground">
          No variables match &ldquo;{query}&rdquo;.
        </p>
      ) : (
        <div className="flex flex-col gap-1">
          {modules.map((module) => {
            // While searching, show every group open so matches are never hidden.
            const isOpen = query.trim() ? true : !!expanded[module];
            const vars = grouped[module] ?? [];
            return (
              <div key={module} className="overflow-hidden rounded-lg border border-border">
                <button
                  type="button"
                  onClick={() => toggle(module)}
                  className="flex w-full items-center justify-between bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/50"
                >
                  <span className="text-xs font-semibold capitalize text-foreground">{module}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                      {vars.length}
                    </span>
                    <FontAwesomeIcon
                      icon={isOpen ? faChevronDown : faChevronRight}
                      className="h-2.5 w-2.5 text-muted-foreground"
                    />
                  </div>
                </button>
                {isOpen && (
                  <div className="divide-y divide-border/50">
                    {vars.map((v) => (
                      <button
                        key={v.key}
                        type="button"
                        onClick={() => onInsert(v.key)}
                        className="group flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-primary/5"
                        title={v.description ?? `Insert {{${v.key}}}`}
                      >
                        <div className="flex w-full items-center justify-between gap-2">
                          <span className="font-mono text-[11px] text-primary group-hover:underline">
                            {`{{${v.key}}}`}
                          </span>
                          <FontAwesomeIcon
                            icon={faCopy}
                            className="h-2.5 w-2.5 text-muted-foreground/50 group-hover:text-primary"
                          />
                        </div>
                        <span className="text-[11px] text-muted-foreground">{v.label}</span>
                        {v.exampleValue && (
                          <span className="text-[10px] italic text-muted-foreground/60">
                            e.g. {v.exampleValue}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
